import aliases from "./pagePathAliases.json";
import menuSlugToCanonical from "./menuSlugToCanonicalPath.json";

/** Maps new menu pageSlug values to legacy PageInformation.pagePath keys. */
export function resolvePagePath(pagePath) {
  if (!pagePath || typeof pagePath !== "string") return pagePath;
  let resolved = pagePath.trim();
  const seen = new Set();

  while (resolved && !seen.has(resolved)) {
    seen.add(resolved);
    const direct = aliases[resolved];
    if (direct) {
      resolved = direct;
      continue;
    }
    const lower = resolved.toLowerCase();
    const lowerMatch = aliases[lower];
    if (lowerMatch) {
      resolved = lowerMatch;
      continue;
    }
    const matchKey = Object.keys(aliases).find((k) => k.toLowerCase() === lower);
    if (matchKey && aliases[matchKey]) {
      resolved = aliases[matchKey];
      continue;
    }
    break;
  }

  return resolved;
}

/* Commenting the following code for progress.ray using AI Agent. 29/07/2026. Comment Start */
// Rewrote menu slugs to short canonicalPath, which breaks MenuSlugRouter-only lessons
// (Go to What is it? / Resume landed on ChapterRootOverview).
// export function toNavigableProgressPath(pathOrSlug) {
//   if (!pathOrSlug || typeof pathOrSlug !== "string") return pathOrSlug;
//   const raw = pathOrSlug.trim();
//   if (!raw || raw === "#") return raw;
//   const bare = raw.replace(/^\/+/, "");
//   if (!bare) return raw;
//
//   const byMenuSlug = menuSlugToCanonical[bare];
//   if (byMenuSlug?.canonicalPath) return byMenuSlug.canonicalPath;
//
//   const lower = bare.toLowerCase();
//   const byPageSlug = Object.values(menuSlugToCanonical).find(
//     (entry) =>
//       entry?.pageSlug &&
//       String(entry.pageSlug).toLowerCase() === lower
//   );
//   if (byPageSlug?.canonicalPath) return byPageSlug.canonicalPath;
//
//   const aliased = resolvePagePath(bare);
//   if (aliased && aliased !== bare) {
//     return `/${String(aliased).replace(/^\/+/, "")}`;
//   }
//
//   return raw.startsWith("/") ? raw : `/${bare}`;
// }
/* Commenting end. ray using AI Agent. 29/07/2026 */
/* Commenting the following code for progress.ray using AI Agent. 29/07/2026. Comment Start */
// Earlier revision still forward-aliased ch-* folder slugs to short legacy paths
// (e.g. …-heuristics-based-methods → /methods), so hub / sidebar links left the new menu tree.
// export function toNavigableProgressPath(pathOrSlug) { ... keep menuSlug / pageSlug / canonical ...
//   const aliased = resolvePagePath(bare); return toNavigableProgressPath(aliasedBare); }
/* Commenting end. ray using AI Agent. 29/07/2026 */
/* Added the following code for progress.ray using AI Agent. 29/07/2026. Code Start */
/**
 * Turn a progress unitKey / stale pageSlug / canonical path into a live URL.
 * Prefer unique ch-* / menu-slug URLs used by MenuSlugRouter. Never rewrite those
 * to short legacy aliases (methods, nlp-foundation, …).
 */
export function toNavigableProgressPath(pathOrSlug) {
  if (!pathOrSlug || typeof pathOrSlug !== "string") return pathOrSlug;
  const raw = pathOrSlug.trim();
  if (!raw || raw === "#") return raw;
  const bare = raw.replace(/^\/+/, "");
  if (!bare) return raw;

  const withSlash = (slug) => `/${String(slug).replace(/^\/+/, "")}`;

  // New menu tree URLs (leaf or folder) — keep as-is; do not short-alias.
  if (/^ch-\d/i.test(bare)) {
    return withSlash(bare);
  }

  // Leaf registry key — keep for MenuSlugRouter.
  if (menuSlugToCanonical[bare]?.canonicalPath) {
    return withSlash(bare);
  }

  const lower = bare.toLowerCase();

  // pageSlug → that leaf's menu-slug URL
  const byPageSlug = Object.entries(menuSlugToCanonical).find(
    ([, entry]) =>
      entry?.pageSlug &&
      String(entry.pageSlug).toLowerCase() === lower
  );
  if (byPageSlug) return withSlash(byPageSlug[0]);

  // Short canonical in the URL bar → map back to a menu slug when possible
  const byCanonical = Object.entries(menuSlugToCanonical).find(
    ([, entry]) =>
      entry?.canonicalPath &&
      String(entry.canonicalPath).replace(/^\/+/, "").toLowerCase() === lower
  );
  if (byCanonical) return withSlash(byCanonical[0]);

  // Short legacy hub/page alias target → prefer the ch-* menu slug that maps to it
  let reverseCh = null;
  for (const [key, value] of Object.entries(aliases)) {
    if (!/^ch-\d/i.test(key)) continue;
    if (String(value).replace(/^\/+/, "").toLowerCase() !== lower) continue;
    if (!reverseCh || key.length > reverseCh.length) reverseCh = key;
  }
  if (reverseCh) return withSlash(reverseCh);

  // Legacy pageSlug aliases (e.g. what-is-nlp → whatis-NLP), then resolve again
  const aliased = resolvePagePath(bare);
  if (aliased && aliased !== bare) {
    const aliasedBare = String(aliased).replace(/^\/+/, "");
    if (aliasedBare && aliasedBare.toLowerCase() !== lower) {
      return toNavigableProgressPath(aliasedBare);
    }
  }

  return raw.startsWith("/") ? raw : withSlash(bare);
}
/* Code end. ray using AI Agent. 29/07/2026 */
